import { CoreEnums } from '@/utils/domain/constants';
import { toQuery } from '@/utils/infrastructure';
import request from '@/utils/infrastructure/communication/request';

const DIAGRAM_TOKEN_STORAGE_PREFIX = 'applications:diagramToken';

// Cache key when versionId is known at call time (precise key)
const makeVersionedKey = (processId, versionId) =>
  `${DIAGRAM_TOKEN_STORAGE_PREFIX}:${processId}:${versionId}`;

// Cache key when versionId is not known at call time (backend resolves current version)
const makeAccessModeKey = (processId, accessMode) =>
  `processConfig:token:${processId}:${accessMode}`;

const readStoredDiagramToken = (processId, versionId) => {
  if (typeof window === 'undefined' || !processId || !versionId) return null;
  try {
    const raw = window.sessionStorage?.getItem(makeVersionedKey(processId, versionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || parsed?.processId !== processId || parsed?.versionId !== versionId) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeStoredDiagramToken = (tokenData) => {
  if (typeof window === 'undefined' || !tokenData?.processId || !tokenData?.versionId) return;
  try {
    window.sessionStorage?.setItem(
      makeVersionedKey(tokenData.processId, tokenData.versionId),
      JSON.stringify(tokenData),
    );
  } catch (error) {
    // Silent fail to avoid breaking UI
  }
};

const readStoredAccessModeToken = (processId, accessMode) => {
  if (typeof window === 'undefined' || !processId || !accessMode) return null;
  try {
    const raw = window.sessionStorage?.getItem(makeAccessModeKey(processId, accessMode));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || parsed?.processId !== processId || parsed?.accessMode !== accessMode) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeStoredAccessModeToken = (tokenData) => {
  if (typeof window === 'undefined' || !tokenData?.processId || !tokenData?.accessMode) return;
  try {
    window.sessionStorage?.setItem(
      makeAccessModeKey(tokenData.processId, tokenData.accessMode),
      JSON.stringify(tokenData),
    );
  } catch (error) {
    // Silent fail to avoid breaking UI
  }
};

export async function generateProcessConfigToken(processId, accessMode, versionId) {
  const resolvedVersionId = versionId;

  if (accessMode === CoreEnums.AccessMode.ReadOnly && processId) {
    if (resolvedVersionId) {
      // Caller knows the exact version — use the precise versioned cache key
      const cachedToken = readStoredDiagramToken(processId, resolvedVersionId);
      if (cachedToken?.token) {
        return cachedToken;
      }
    } else {
      // Caller omits versionId (backend resolves current version) — use processId+accessMode key
      const cachedToken = readStoredAccessModeToken(processId, accessMode);
      if (cachedToken?.token) {
        return cachedToken;
      }
    }
  }

  const url = resolvedVersionId
    ? `/process/config/generateToken/${processId}/${accessMode}/${resolvedVersionId}`
    : `/process/config/generateToken/${processId}/${accessMode}`;
  const response = await request.post(url);

  if (accessMode === CoreEnums.AccessMode.ReadOnly && processId) {
    const storedToken = {
      token: response?.token,
      versionId: resolvedVersionId || response?.versionId,
      versionNumber: response?.versionNumber,
      accessMode,
      processId,
    };

    if (storedToken.token) {
      if (resolvedVersionId) {
        // Write to the precise versioned key (existing path, unchanged)
        if (storedToken.versionId) {
          writeStoredDiagramToken(storedToken);
        }
      } else {
        // Write to the processId+accessMode key so future no-versionId calls hit cache
        writeStoredAccessModeToken(storedToken);
        // Also write to the versioned key if the response included a versionId,
        // so callers that do pass versionId can benefit too
        if (storedToken.versionId) {
          writeStoredDiagramToken(storedToken);
        }
      }
    }
  }

  return response;
}

export async function queryListPermissions(payload) {
  return request(`/${payload.route}`, {
    params: toQuery(payload.params),
  });
}

export async function querySavePermission(payload) {
  return request.post(`/process/${payload.routeAdd}/${payload.id}`, {
    data: payload.data,
  });
}
